// Optional "Continue with Microsoft": signs in with the TELLEC NFR sign-in app
// (MSAL, delegated User.Read only) and reads the person's Entra profile from
// Microsoft Graph, so they only have to pick how many client tenants they
// manage. The ID token goes to the API, which checks Microsoft's signature
// before treating the email and tenant ID as verified.
//
// The app's client ID is in /config/signin.json. While it's empty the button
// is hidden and the form works as before. "mock" (local dev) fakes it all.

import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export interface MsProfile {
  name: string;
  email: string;
  mobile: string;
  jobTitle: string;
  company: string;
  tenantId: string;
  /** Sent to the API as proof of the email and tenant; valid for about an hour. */
  idToken: string;
}

interface GraphMe {
  displayName?: string;
  mail?: string | null;
  mobilePhone?: string | null;
  businessPhones?: string[];
  jobTitle?: string | null;
  companyName?: string | null;
}

const SCOPES = ['User.Read'];
const GRAPH = 'https://graph.microsoft.com/v1.0';
const MOCK_KEY = 'tellec-nfr-mock-ms';

let configPromise: Promise<string> | null = null;
let appPromise: Promise<IPublicClientApplication> | null = null;

/** The sign-in app's client ID, '' when sign-in isn't set up yet. */
export function signinClientId(): Promise<string> {
  configPromise ??= fetch('/config/signin.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : {}))
    .then((c: { clientId?: string }) => (typeof c.clientId === 'string' ? c.clientId.trim() : ''))
    .catch(() => '');
  return configPromise;
}

async function app(): Promise<IPublicClientApplication> {
  appPromise ??= (async () => {
    const clientId = await signinClientId();
    // Loaded on demand so the landing page doesn't carry MSAL.
    const { createStandardPublicClientApplication } = await import('@azure/msal-browser');
    return createStandardPublicClientApplication({
      auth: {
        clientId,
        // Work and school accounts only: personal accounts have no tenant to audit.
        authority: 'https://login.microsoftonline.com/organizations',
        redirectUri: `${window.location.origin}/details`,
        postLogoutRedirectUri: `${window.location.origin}/`,
      },
      // Per tab, and gone when the tab closes (shared tablets at the table).
      cache: { cacheLocation: 'sessionStorage' },
    });
  })();
  return appPromise;
}

export async function signIn(): Promise<void> {
  const clientId = await signinClientId();
  if (clientId === 'mock') {
    sessionStorage.setItem(MOCK_KEY, '1');
    window.location.assign('/details');
    return;
  }
  const pca = await app();
  // Always show the account picker, so a shared device never reuses the last person.
  await pca.loginRedirect({ scopes: SCOPES, prompt: 'select_account' });
}

/** Signs out of Microsoft too, so the next person on a shared device starts clean. */
export async function signOut(returnTo = '/'): Promise<void> {
  const clientId = await signinClientId();
  if (clientId === 'mock') {
    sessionStorage.removeItem(MOCK_KEY);
    window.location.assign(returnTo);
    return;
  }
  if (!clientId) return;
  const pca = await app();
  await pca.handleRedirectPromise().catch(() => null);
  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (!account) return;
  await pca.logoutRedirect({ account, postLogoutRedirectUri: `${window.location.origin}${returnTo}` });
}

/** Forgets the sign-in in this tab without leaving the page (no Microsoft sign-out). */
export async function forgetMicrosoft(): Promise<void> {
  const clientId = await signinClientId();
  if (clientId === 'mock') sessionStorage.removeItem(MOCK_KEY);
  if (!clientId || clientId === 'mock') return;
  const pca = await app();
  await pca.clearCache().catch(() => undefined);
}

/** True if this tab is signed in (without loading MSAL when sign-in isn't set up). */
export async function isSignedIn(): Promise<boolean> {
  const clientId = await signinClientId();
  if (clientId === 'mock') return sessionStorage.getItem(MOCK_KEY) === '1';
  if (!clientId) return false;
  const pca = await app();
  return pca.getAllAccounts().length > 0;
}

/**
 * Finishes a sign-in redirect if there is one and returns the profile, or null
 * if the person hasn't signed in (or it failed: they can still type it in).
 * A cached account is only reused with allowCached, i.e. for the person who
 * signed in during this sign-up, never for whoever picks up the device next.
 */
export async function getMsProfile(allowCached = false): Promise<MsProfile | null> {
  const clientId = await signinClientId();
  if (!clientId) return null;
  if (clientId === 'mock') return sessionStorage.getItem(MOCK_KEY) === '1' ? MOCK_PROFILE : null;
  try {
    const pca = await app();
    const redirect = await pca.handleRedirectPromise();
    const account: AccountInfo | undefined =
      redirect?.account ?? (allowCached ? (pca.getActiveAccount() ?? pca.getAllAccounts()[0]) : undefined);
    if (!account) return null;
    pca.setActiveAccount(account);
    const auth = await pca.acquireTokenSilent({ scopes: SCOPES, account });
    const headers = { Authorization: `Bearer ${auth.accessToken}` };
    const [me, org]: [GraphMe, { value?: { displayName?: string }[] }] = await Promise.all([
      fetch(`${GRAPH}/me?$select=displayName,mail,userPrincipalName,mobilePhone,businessPhones,jobTitle,companyName`, { headers }).then(
        (r) => (r.ok ? r.json() : {}),
      ),
      // The tenant's own name, for when the profile has no company name.
      fetch(`${GRAPH}/organization?$select=displayName`, { headers })
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({})),
    ]);
    const claims = (auth.idTokenClaims ?? {}) as { email?: string; tid?: string };
    return {
      name: str(me.displayName) || account.name || '',
      email: str(me.mail) || str(claims.email) || account.username,
      mobile: str(me.mobilePhone) || str(me.businessPhones?.[0]),
      jobTitle: str(me.jobTitle),
      company: str(me.companyName) || str(org.value?.[0]?.displayName),
      tenantId: claims.tid ?? account.tenantId,
      idToken: auth.idToken,
    };
  } catch {
    return null;
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

const MOCK_PROFILE: MsProfile = {
  name: 'Morgan Admin',
  email: 'morgan@contoso-msp.com.au',
  mobile: '+61 412 555 019',
  jobTitle: 'Head of Service Delivery',
  company: 'Contoso MSP',
  tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47',
  idToken: 'ms:morgan@contoso-msp.com.au:72f988bf-86f1-41af-91ab-2d7cd011db47',
};
